import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

function distanceKm(lat1, lng1, lat2, lng2) {
  if (!lat1 || !lng1 || !lat2 || !lng2) return null;
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2)*Math.sin(dLat/2) +
    Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*
    Math.sin(dLng/2)*Math.sin(dLng/2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

function minutesAgo(ts) {
  if (!ts) return null;
  return Math.round((Date.now() - new Date(ts).getTime()) / 60000);
}

export default function LiveMapPage({ onBack }) {
  const http = api();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await http.get('/employer/live-map');
      setWorkers(res.data || []);
      setLastRefresh(new Date());
    } catch (e) {
      setWorkers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [load]);

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        {onBack && (
          <button onClick={onBack} className="p-2 rounded-xl hover:bg-neutral-100 transition">
            <svg className="w-5 h-5 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
        )}
        <div className="flex-1">
          <h2 className="font-bold text-neutral-900 text-lg">Live Worker Map 🗺️</h2>
          <p className="text-xs text-neutral-400">
            {lastRefresh ? `Updated ${minutesAgo(lastRefresh) === 0 ? 'just now' : `${minutesAgo(lastRefresh)}m ago`}` : 'Loading...'}
          </p>
        </div>
        <button
          onClick={load}
          className="px-3 py-1.5 bg-brand-50 text-brand-600 text-xs font-semibold rounded-lg hover:bg-brand-100 transition"
        >
          ↻ Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-7 h-7 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : workers.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-neutral-100 shadow-sm">
          <div className="text-5xl mb-3">🗺️</div>
          <h3 className="font-bold text-neutral-700 mb-1">No workers currently clocked in</h3>
          <p className="text-sm text-neutral-400">Workers will appear here when they clock in for today's shifts</p>
        </div>
      ) : (
        <>
          {/* Summary bar */}
          <div className="bg-brand-50 border border-brand-200 rounded-xl p-3 mb-4 flex items-center gap-3">
            <span className="text-2xl">👷</span>
            <div>
              <p className="font-bold text-brand-800 text-sm">{workers.length} worker{workers.length !== 1 ? 's' : ''} on shift</p>
              <p className="text-xs text-brand-600">Auto-refreshes every 60 seconds</p>
            </div>
          </div>

          {/* Map placeholder + worker grid */}
          <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-4 mb-4">
            <div className="bg-neutral-50 rounded-xl p-4 text-center text-neutral-400 text-sm mb-4 border border-dashed border-neutral-200">
              📍 Interactive map — worker positions shown below
            </div>

            <div className="grid grid-cols-1 gap-3">
              {workers.map(worker => {
                const dist = distanceKm(
                  parseFloat(worker.lat), parseFloat(worker.lng),
                  parseFloat(worker.shift_lat), parseFloat(worker.shift_lng)
                );
                const isLate = dist !== null && dist > 1; // >1km from shift = late
                const minsAgo = minutesAgo(worker.checked_in_at);

                return (
                  <div
                    key={worker.hire_id}
                    className={`rounded-xl p-4 border ${isLate ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Avatar */}
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {worker.worker_photo
                          ? <img src={worker.worker_photo} alt="" className="w-full h-full rounded-full object-cover" />
                          : (worker.worker_name || 'W')[0].toUpperCase()
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-neutral-900 text-sm">{worker.worker_name}</p>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${isLate ? 'bg-orange-200 text-orange-800' : 'bg-green-200 text-green-800'}`}>
                            {isLate ? '🟠 Away from site' : '🟢 On site'}
                          </span>
                        </div>
                        <p className="text-xs text-neutral-600 truncate">{worker.shift_title}</p>
                        <p className="text-xs text-neutral-400">📍 {worker.shift_location}</p>
                        <div className="flex gap-3 mt-1 text-xs text-neutral-500">
                          <span>⏱ Clocked in {minsAgo}m ago</span>
                          {dist !== null && (
                            <span>📏 {dist < 0.1 ? '<100m' : `${dist.toFixed(1)}km`} from shift</span>
                          )}
                          {worker.lat && worker.lng && (
                            <a
                              href={`https://maps.google.com/?q=${worker.lat},${worker.lng}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-brand-600 font-semibold hover:underline"
                            >
                              View on Maps ↗
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="flex gap-3 text-xs text-neutral-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span> On site</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500 inline-block"></span> More than 15 min from site</span>
          </div>
        </>
      )}
    </div>
  );
}
